import { KeycloakService } from 'keycloak-angular';
import type { KeycloakInitOptions } from 'keycloak-js';
import { AppConfigService } from '../services/app-config.service';
import {
  ElxKeycloakService,
  PersistedKeycloakTokens,
} from '../services/keycloak.service';

const KEYCLOAK_INIT_OPTIONS = (
  redirectUri: string,
  onLoad?: 'check-sso' | 'login-required',
  persistedTokens?: PersistedKeycloakTokens,
  production?: boolean,
  authSource?: 'sso' | 'backend',
): KeycloakInitOptions => {
  // If the token is from backend, do NOT pass it to Keycloak adapter
  // The adapter will fail to validate it or try to refresh it against Keycloak, which is wrong.
  const isBackend = authSource === 'backend';

  return {
    pkceMethod: 'S256',
    redirectUri,
    scope: 'openid profile email',
    onLoad,
    // Enables silent SSO check on refresh without redirect loops.
    // Requires CSP to allow an iframe navigation to the Keycloak origin.
    silentCheckSsoRedirectUri:
      typeof window !== 'undefined'
        ? `${window.location.origin}/assets/silent-check-sso.html`
        : undefined,
    // Bound the silent-check-sso iframe wait so the app boot is never held
    // hostage by a slow/blocked Keycloak round-trip (guideline #7 Performance).
    // Default in keycloak-js is 10s; we used 45s previously which caused
    // ~30–45s white-screen refreshes when the iframe stalled. 8s is enough
    // for a healthy round trip and bounds the worst case.
    messageReceiveTimeout: 8000,
    enableLogging: true, // Keycloak logging enabled by default in runtime config approach
    token: isBackend ? undefined : persistedTokens?.token,
    refreshToken: isBackend ? undefined : persistedTokens?.refreshToken,
    idToken: isBackend ? undefined : persistedTokens?.idToken,
  };
};

export function initializeKeycloak(
  keycloak: KeycloakService,
  appConfig: AppConfigService,
  elxKeycloak: ElxKeycloakService,
) {
  return async () => {
    // Guideline 5 (Error Handling & Robustness): load config up front to avoid race conditions
    await appConfig.loadConfig();

    const config = {
      url: appConfig.keycloakUrl,
      realm: appConfig.keycloakRealm,
      clientId: appConfig.clientId,
    };

    // Guideline 5: always validate external configuration before bootstrapping security clients
    if (!config.url || !config.realm || !config.clientId) {
            // Mark initialization as completed so the app can proceed to local login
      (window as any).__keycloakInitCompleted = true;
      elxKeycloak.markInitialized(false);
      return false;
    }

    const currentUrl =
      typeof window !== 'undefined'
        ? window.location.pathname + window.location.search
        : '';

    // Public auth pages don't need a Keycloak boot. They drive their own
    // flows (LoginComponent calls keycloakLibrary.login(...) lazily when the
    // user clicks SSO). Skipping init here avoids a multi-second boot wait
    // right after logout/redirect to /login (guideline #7 Performance).
    const isPublicAuthRoute =
      currentUrl.startsWith('/login') ||
      currentUrl.startsWith('/register') ||
      currentUrl.startsWith('/forgot-password') ||
      currentUrl.startsWith('/reset-password') ||
      currentUrl.startsWith('/verify-email') ||
      currentUrl.startsWith('/check-email');

    if (isPublicAuthRoute) {
      (window as any).__keycloakInitCompleted = true;
      elxKeycloak.markInitialized(false);
      return true;
    }

    // Check if we're in Keycloak callback flow - don't store these URLs
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));

    // If Keycloak returned an OAuth error (e.g. login_required), don't keep retrying SSO.
    // Clear any persisted tokens, clean the URL, and allow the app to continue to login.
    const hasOAuthError =
      urlParams.has('error') ||
      hashParams.has('error') ||
      urlParams.has('error_description') ||
      hashParams.has('error_description');
    if (hasOAuthError) {
      try {
        elxKeycloak.clearKeycloakAdapterLocalState();
        elxKeycloak.clearPersistedTokens();
        elxKeycloak.markInitialized(false);
        (window as any).__keycloakInitCompleted = true;
      } catch {}

      try {
        // Remove error fragments/query without a navigation.
        const cleanUrl = window.location.pathname + window.location.search;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch {}

      return false;
    }

    const isKeycloakCallback =
      urlParams.has('code') ||
      urlParams.has('state') ||
      hashParams.has('code') ||
      hashParams.has('state') ||
      window.location.href.includes('/auth/realms/');

    // Only attempt to store a new redirect URL if it's NOT a callback flow
    // Storing during callback would store the URL with temporary codes/states
    if (!isKeycloakCallback) {
      elxKeycloak.clearKeycloakAdapterLocalState();
      const normalizedUrl = currentUrl.split('?')[0];
      const excludedPaths = [
        '/error/401',
        '/error/403',
        '/error/404',
        '/error/500',
        '/register',
        '/login',
        '/',
      ];

      // Only store valid URLs that are not error pages or default home
      const isExcluded = excludedPaths.some((path) => {
        if (path === '/') return normalizedUrl === '/';
        return normalizedUrl.startsWith(path);
      });

      if (
        !isExcluded &&
        currentUrl.startsWith('/') &&
        !currentUrl.includes('//') &&
        !/[^\/\w\-\.\?=\&%@\+:,]/.test(currentUrl)
      ) {
        sessionStorage.setItem('postAuthRedirect', currentUrl);
      } else {
        if (normalizedUrl === '/login' || normalizedUrl.startsWith('/error/')) {
          sessionStorage.removeItem('postAuthRedirect');
        }
      }
    }

    const persistedTokens = elxKeycloak.restorePersistedTokens();

    // Fast path (guideline #7 Performance):
    // If we already have a valid (or refreshable) token cached and we are NOT
    // in the middle of a Keycloak code/state callback, there is no need to
    // run the silent-check-sso iframe handshake at boot — it is the single
    // largest source of slow refreshes (5–45s round trip + iframe init).
    // We let the app start immediately with the cached token and rely on the
    // background refresh loop to keep it fresh. Real session expiry is still
    // handled by the HTTP error interceptor + SessionService.
    if (!isKeycloakCallback && elxKeycloak.hasValidPersistedToken()) {
      try {
        elxKeycloak.markInitialized(true);
        (window as any).__keycloakInitCompleted = true;
        elxKeycloak.startAutomaticTokenRefresh();
      } catch {}
      return true;
    }

    // Single OAuth redirect URI (origin + / from runtime config, corrected to this host).
    // Per-path redirect_uri breaks silent check / login when the Keycloak client only
    // allows the app root or a fixed callback; postAuthRedirect restores deep links.
    const redirectUri = appConfig.keycloakRedirectUri;

    // Run the silent-check-sso iframe ONLY when we are mid-callback or when
    // we already have an SSO token to validate. In every other case the
    // guards will redirect to /login anyway — paying 5–8s on every boot to
    // *maybe* discover a KC session is not worth the latency cost
    // (guideline #7 Performance). Users who want SSO can click the SSO
    // button on /login, which triggers a direct redirect (no iframe).
    const shouldCheckSso =
      isKeycloakCallback ||
      (persistedTokens?.token && persistedTokens?.authSource !== 'backend');
    const onLoad: 'check-sso' | undefined = shouldCheckSso
      ? 'check-sso'
      : undefined;

    const initOptions = KEYCLOAK_INIT_OPTIONS(
      redirectUri,
      onLoad,
      persistedTokens,
      appConfig.production,
      persistedTokens?.authSource,
    );

    let authenticated = false;
    try {
      authenticated = await keycloak.init({
        config,
        initOptions,
        loadUserProfileAtStartUp: false,
        enableBearerInterceptor: false,
        bearerExcludedUrls: [],
      });
    } catch (err) {
      
      // Guideline 5 (Robustness): Keycloak init can fail for many transient
      // reasons (silent-check-sso iframe blocked, slow KC, network blip).
      // For backend-auth users the token has nothing to do with Keycloak,
      // and SSO users may still hold a valid (or refreshable) token. Don't
      // sign them out just because the SSO bootstrap stumbled.
      elxKeycloak.clearKeycloakAdapterLocalState();

      const canKeepSession = elxKeycloak.hasValidPersistedToken();
      if (canKeepSession) {
                elxKeycloak.markInitialized(true);
        (window as any).__keycloakInitCompleted = true;
        try {
          elxKeycloak.startAutomaticTokenRefresh();
        } catch {}
        return true;
      }

      elxKeycloak.clearPersistedTokens();
      elxKeycloak.markInitialized(false);
      (window as any).__keycloakInitCompleted = true;
      return false;
    }

    (window as any).__keycloakInitCompleted = true;

    const hasBackendTokens =
      persistedTokens?.token && persistedTokens?.authSource === 'backend';

    if (authenticated || hasBackendTokens) {
      await elxKeycloak.afterSuccessfulInit();
      elxKeycloak.startAutomaticTokenRefresh();
    } else {
      elxKeycloak.markInitialized(false);
    }

    return authenticated;
  };
}
